import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Draggable from "react-draggable";
import LoadingOverlay from "react-loading-overlay";
import ReactTooltip from "react-tooltip";
import Portal from "../../../common/components/Portal";
import { EllipsisTooltipText } from "../../../common/components/EllipsisTextWithTooltip";
import Loader from "../../../common/components/Loader";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import {
    bandSelectionKey,
    buildExecutiveSummaryHierarchyTilesMap,
    buildExecutiveSummaryHierarchyOrderMap,
    buildExecutiveSummarySelectionForest,
    buildMultiBuildingGroupedExecutiveSummaryTile,
    collectExecutiveSummaryDisabledSelectionIds,
    collectDescendantSelIds,
    collectInnerDescendantSelIds,
    EXEC_SUM_TILE_SCOPE,
    getExecutiveSummaryTileScope,
    getRowCheckboxState,
    hasDescendantInSelection
} from "../../utils/executiveSummarySelectionUtils";
import "../../executiveSummaryHierarchyTile.css";

function collectParentSelIds(nodes) {
    const out = [];
    const walk = ns => {
        for (const n of ns || []) {
            if (n.band?.length) {
                out.push(bandSelectionKey(n));
                walk(n.band);
            }
        }
    };
    walk(nodes);
    return out;
}

function buildHierarchyLookupMaps(nodes) {
    const parentBySelId = new Map();
    const selIdToNode = new Map();
    const walk = (ns, parentSelId) => {
        for (const n of ns || []) {
            const k = bandSelectionKey(n);
            selIdToNode.set(k, n);
            if (parentSelId != null) parentBySelId.set(k, parentSelId);
            if (n.band?.length) walk(n.band, k);
        }
    };
    walk(nodes, null);
    return { parentBySelId, selIdToNode };
}

function areSetsEqual(left, right) {
    if (left?.size !== right?.size) return false;
    for (const item of left) {
        if (!right.has(item)) return false;
    }
    return true;
}

function useInnerSelectAllState(node, selected, disabledIds) {
    return useMemo(() => {
        const innerIds = collectInnerDescendantSelIds(node).filter(id => !disabledIds?.has?.(id));
        if (!innerIds.length) {
            return { innerIds, checked: false, indeterminate: false, disabled: true, selCount: 0, total: 0 };
        }
        const picked = innerIds.filter(id => selected.has(id));
        return {
            innerIds,
            checked: picked.length === innerIds.length,
            indeterminate: picked.length > 0 && picked.length < innerIds.length,
            disabled: false,
            selCount: picked.length,
            total: innerIds.length
        };
    }, [node, selected, disabledIds]);
}

const SELECTION_TOOLTIP_ID = "executive-summary-tile-selection-tooltip";

function HierarchyTreeRow({ node, selected, onToggle, disabledIds, isExpanded, onToggleExpand, onToggleInnerSelectAll, isNested }) {
    const { checked, indeterminate } = getRowCheckboxState(node, selected);
    const selfRef = useRef(null);
    const innerRef = useRef(null);
    const hasChildren = !!(node.band && node.band.length);
    const selfDisabled = disabledIds?.has?.(bandSelectionKey(node));
    const {
        checked: innerChecked,
        indeterminate: innerIndeterminate,
        disabled: innerDisabled,
        selCount,
        total
    } = useInnerSelectAllState(node, selected, disabledIds);

    useEffect(() => {
        if (selfRef?.current) selfRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    useEffect(() => {
        if (innerRef?.current) innerRef.current.indeterminate = innerIndeterminate;
    }, [innerIndeterminate]);

    const typeLabel = node.band_type === "group" ? "Group" : node.band_type === "image" ? "Image band" : "Text";
    const buildingCountLabel = node?.building_count;

    const handleBandRowClick = useCallback(
        e => {
            if (!hasChildren) return;
            if (e.target.closest(".es-hier-sel-band-check")) return;
            if (e.target.closest(".es-hier-sel-band-acc")) return;
            onToggleExpand(bandSelectionKey(node));
        },
        [hasChildren, node, onToggleExpand]
    );

    return (
        <div
            className={`es-hier-sel-band es-hier-sel-band--${node.band_type}${hasChildren ? " es-hier-sel-band--parent" : ""}${isNested ? " es-hier-sel-band--nested es-hier-sel-band--inner-band" : " es-hier-sel-band--outer-band"}`}
        >
            <div className={`es-hier-sel-band-row${hasChildren ? " es-hier-sel-band-row--accordion" : ""}`} onClick={handleBandRowClick}>
                <div className="es-hier-sel-band-left">
                    <label className="container-check green-check mb-0 es-hier-sel-band-check" data-for={SELECTION_TOOLTIP_ID}>
                        <input
                            type="checkbox"
                            checked={checked}
                            ref={selfRef}
                            onChange={e => {
                                onToggle(node, e.target.checked);
                            }}
                        />
                        <span className={`checkmark ${selfDisabled ? "cursor-disabled" : ""}`} />
                    </label>
                    <div className="es-hier-sel-band-label-wrapper">
                        <EllipsisTooltipText text={node.name} className="es-hier-sel-band-name" />
                    </div>
                </div>
                <div className="es-hier-sel-band-right">
                    {hasChildren && total > 0 ? (
                        <span className="es-hier-sel-band-count">
                            Selected ({selCount}/{total})
                        </span>
                    ) : null}

                    {buildingCountLabel ? (
                        <span className="es-hier-sel-band-building-count">
                            <span className="es-hier-sel-band-building-count-label">Buildings : </span>
                            <span className="es-hier-sel-band-building-count-value">{buildingCountLabel}</span>
                        </span>
                    ) : null}
                    {hasChildren ? (
                        <label className={`container-check green-check mb-0 es-hier-sel-band-check es-hier-sel-band-check--inner `}>
                            <input
                                type="checkbox"
                                checked={innerChecked}
                                ref={innerRef}
                                onChange={e => {
                                    if (!innerDisabled) onToggleInnerSelectAll(node, e.target.checked);
                                }}
                            />
                            <span className={`checkmark ${innerDisabled ? "cursor-disabled" : ""}`} data-for={SELECTION_TOOLTIP_ID} />
                            <span className="es-hier-sel-band-inner-text">Select All</span>
                        </label>
                    ) : null}
                    <span
                        className={`es-hier-sel-band-type-badge ${
                            node.band_type === "group"
                                ? "es-hier-sel-band-type-badge-group"
                                : node.band_type === "image"
                                  ? "es-hier-sel-band-type-badge-image"
                                  : "es-hier-sel-band-type-badge-text"
                        }`}
                    >
                        {typeLabel}
                    </span>
                    {hasChildren ? (
                        <button
                            type="button"
                            className="es-hier-sel-band-acc"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? "Collapse nested bands" : "Expand nested bands"}
                            onClick={e => {
                                e.stopPropagation();
                                onToggleExpand(bandSelectionKey(node));
                            }}
                        >
                            <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`} aria-hidden="true" />
                        </button>
                    ) : (
                        <span className="es-hier-sel-band-acc-spacer" aria-hidden="true" />
                    )}
                </div>
            </div>
        </div>
    );
}

function HierarchyTree({ nodes, depth, selected, onToggle, disabledIds, collapsedIds, onToggleExpand, onToggleInnerSelectAll }) {
    if (!nodes?.length) return null;
    return (
        <div className={`es-hier-sel-tree-level${depth > 0 ? " es-hier-sel-tree-level--nested" : ""}`}>
            {nodes.map(n => {
                const rowKey = bandSelectionKey(n);
                const hasChildren = !!(n.band && n.band.length);
                const isExpanded = hasChildren && !collapsedIds.has(rowKey);
                return (
                    <div key={rowKey} className={`es-hier-sel-node ${depth === 0 && hasChildren ? "parent-band-row" : ""}`}>
                        <HierarchyTreeRow
                            node={n}
                            selected={selected}
                            onToggle={onToggle}
                            disabledIds={disabledIds}
                            isExpanded={isExpanded}
                            onToggleExpand={onToggleExpand}
                            onToggleInnerSelectAll={onToggleInnerSelectAll}
                            isNested={depth > 0}
                        />
                        {hasChildren ? (
                            <div
                                className={`es-hier-sel-nest${isExpanded ? " is-open" : ""}`}
                                id={`es-hier-panel-${rowKey.replace(/[^a-z0-9]/gi, "_")}`}
                                role="region"
                                aria-hidden={!isExpanded}
                            >
                                <HierarchyTree
                                    nodes={n.band}
                                    depth={depth + 1}
                                    selected={selected}
                                    onToggle={onToggle}
                                    disabledIds={disabledIds}
                                    collapsedIds={collapsedIds}
                                    onToggleExpand={onToggleExpand}
                                    onToggleInnerSelectAll={onToggleInnerSelectAll}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

const ExecutiveSummaryTileSelectionModal = ({
    show = false,
    tilesByKey = {},
    isMultipleBuilding = false,
    addedTileKeys = {},
    isLoading = false,
    onCancel,
    onAdd
}) => {
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(() => new Set());
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [collapsedIds, setCollapsedIds] = useState(() => new Set());
    const globalSelectAllRef = useRef(null);
    const selectionsInitializedRef = useRef(false);

    const groupedForestTree = useMemo(() => buildExecutiveSummarySelectionForest(tilesByKey || {}), [tilesByKey]);
    const executiveSummaryTree = groupedForestTree;

    const filteredForest = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return executiveSummaryTree;
        const filterNodes = nodes =>
            (nodes || [])
                .map(n => {
                    const name = (n.name || n.apiKey || "").toLowerCase();
                    const sub = n.band ? filterNodes(n.band) : [];
                    const selfMatch = name.includes(q);
                    if (selfMatch || sub.length) {
                        return { ...n, band: sub.length ? sub : n.band };
                    }
                    return null;
                })
                .filter(Boolean);
        return filterNodes(executiveSummaryTree);
    }, [executiveSummaryTree, search]);

    const { parentBySelId, selIdToNode } = useMemo(() => buildHierarchyLookupMaps(executiveSummaryTree), [executiveSummaryTree]);

    useEffect(() => {
        if (!show) {
            setSearch("");
            setSelected(new Set());
            setShowCancelConfirmation(false);
            setCollapsedIds(new Set());
            selectionsInitializedRef.current = false;
            return;
        }
        const q = search.trim();
        if (q) {
            setCollapsedIds(new Set());
        } else {
            setCollapsedIds(new Set(collectParentSelIds(filteredForest)));
        }
    }, [show, filteredForest, search]);

    const allSelectableIds = useMemo(() => {
        const ids = [];
        const walk = nodes => {
            (nodes || []).forEach(n => {
                ids.push(...collectDescendantSelIds(n));
            });
        };
        walk(filteredForest);
        return ids;
    }, [filteredForest]);

    const groupedExistingIdSet = useMemo(() => {
        const existingTiles = isMultipleBuilding
            ? Object.fromEntries(
                  Object.entries(addedTileKeys || {}).filter(
                      ([key, tile]) => getExecutiveSummaryTileScope(tile, key) === EXEC_SUM_TILE_SCOPE.MULTI_GROUPED
                  )
              )
            : addedTileKeys || {};
        return collectExecutiveSummaryDisabledSelectionIds(groupedForestTree, existingTiles);
    }, [groupedForestTree, addedTileKeys, isMultipleBuilding]);

    useEffect(() => {
        if (!show || isLoading || selectionsInitializedRef.current) return;
        if (!Object.keys(tilesByKey || {}).length) return;
        selectionsInitializedRef.current = true;
        setSelected(new Set(groupedExistingIdSet));
    }, [show, isLoading, tilesByKey, groupedExistingIdSet]);

    const disabledIdSet = useMemo(() => new Set(), []);

    const displaySelected = useMemo(() => {
        return new Set(selected);
    }, [selected]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [disabledIdSet, filteredForest]);

    const availableIds = useMemo(() => allSelectableIds.filter(id => !disabledIdSet.has(id)), [allSelectableIds, disabledIdSet]);

    const isAllSelected = availableIds.length > 0 && availableIds.every(id => selected.has(id));
    const isGlobalPartial = useMemo(() => {
        if (!availableIds.length) return false;
        const n = [...selected].filter(id => availableIds.includes(id)).length;
        return n > 0 && n < availableIds.length;
    }, [availableIds, selected]);

    useEffect(() => {
        if (globalSelectAllRef.current) {
            globalSelectAllRef.current.indeterminate = !isAllSelected && isGlobalPartial;
        }
    }, [isAllSelected, isGlobalPartial]);

    const toggleAllBands = useCallback(
        checked => {
            setSelected(prev => {
                const next = new Set(prev);
                if (checked) {
                    availableIds.forEach(id => next.add(id));
                } else {
                    availableIds.forEach(id => next.delete(id));
                }
                return next;
            });
        },
        [availableIds]
    );

    const toggleNode = useCallback(
        (node, checked) => {
            if (disabledIdSet.has(bandSelectionKey(node))) return;
            setSelected(prev => {
                const next = new Set(prev);
                const k = bandSelectionKey(node);
                if (checked) {
                    collectDescendantSelIds(node)
                        .filter(id => !disabledIdSet.has(id))
                        .forEach(id => next.add(id));
                    let p = parentBySelId.get(k);
                    while (p) {
                        if (!disabledIdSet.has(p)) next.add(p);
                        p = parentBySelId.get(p);
                    }
                } else {
                    const innerIds = collectInnerDescendantSelIds(node);
                    next.delete(k);
                    innerIds?.forEach(id => next.delete(id));
                    let p = parentBySelId.get(k);
                    while (p) {
                        const anc = selIdToNode.get(p);
                        if (!anc) break;
                        if (next.has(p) && !hasDescendantInSelection(anc, next)) {
                            next.delete(p);
                        }
                        p = parentBySelId.get(p);
                    }
                }
                return next;
            });
        },
        [disabledIdSet, parentBySelId, selIdToNode]
    );

    const toggleInnerSelectAll = useCallback(
        (node, checked) => {
            const k = bandSelectionKey(node);

            const innerIds = collectInnerDescendantSelIds(node).filter(id => !disabledIdSet.has(id));
            if (!innerIds.length) return;
            setSelected(prev => {
                const next = new Set(prev);
                if (checked) {
                    if (!disabledIdSet.has(k)) next.add(k);
                    innerIds.forEach(id => next.add(id));
                    let p = parentBySelId.get(k);
                    while (p) {
                        if (!disabledIdSet.has(p)) next.add(p);
                        p = parentBySelId.get(p);
                    }
                } else {
                    innerIds.forEach(id => next.delete(id));
                    next.delete(k);
                    let p = parentBySelId.get(k);
                    while (p) {
                        const anc = selIdToNode.get(p);
                        if (!anc) break;
                        if (next.has(p) && !hasDescendantInSelection(anc, next)) {
                            next.delete(p);
                        }
                        p = parentBySelId.get(p);
                    }
                }
                return next;
            });
        },
        [disabledIdSet, parentBySelId, selIdToNode]
    );

    const handleToggleExpand = useCallback(band_id => {
        setCollapsedIds(prev => {
            const next = new Set(prev);
            if (next.has(band_id)) next.delete(band_id);
            else next.add(band_id);
            return next;
        });
    }, []);

    const hasSelectionChanged = useMemo(() => !areSetsEqual(selected, groupedExistingIdSet), [selected, groupedExistingIdSet]);

    const handleAddSelected = useCallback(() => {
        if (!hasSelectionChanged) return;

        if (isMultipleBuilding) {
            const tiles = buildMultiBuildingGroupedExecutiveSummaryTile(groupedForestTree, selected);
            if (!Object.keys(tiles).length && !hasSelectionChanged) return;
            onAdd?.(tiles, buildExecutiveSummaryHierarchyOrderMap(groupedForestTree), { mode: "multi_combined" });
            return;
        }

        const tiles = buildExecutiveSummaryHierarchyTilesMap(groupedForestTree, selected, apiKey => apiKey);
        if (!Object.keys(tiles).length && !hasSelectionChanged) return;
        onAdd?.(tiles, buildExecutiveSummaryHierarchyOrderMap(groupedForestTree), { mode: "single" });
    }, [isMultipleBuilding, groupedForestTree, selected, hasSelectionChanged, onAdd]);

    const handleCancelClick = useCallback(() => {
        if (hasSelectionChanged) setShowCancelConfirmation(true);
        else onCancel?.();
    }, [hasSelectionChanged, onCancel]);

    const handleConfirmCancel = useCallback(() => {
        setShowCancelConfirmation(false);
        onCancel?.();
    }, [onCancel]);

    if (!show) return null;

    return (
        <>
            <Portal
                body={
                    <div
                        className="modal modal-region modal-view reco-view-mdl column-setting-modal bbi-config-modal executive-summary-tile-selection-modal"
                        style={{ display: "block" }}
                    >
                        <Draggable handle=".draggable" cancel=".non-draggable">
                            <div className="modal-dialog modal-xl executive-summary-tile-selection-dialog" role="document">
                                <div className="modal-content executive-summary-tile-selection-content">
                                    <ReactTooltip id={SELECTION_TOOLTIP_ID} effect="solid" place="bottom" backgroundColor="#007bff" />
                                    <div className="modal-header executive-summary-tile-selection-header draggable">
                                        <div className="executive-summary-tile-selection-title-wrap">
                                            <h5 className="modal-title executive-summary-tile-selection-title">BBI Executive Summary Bands</h5>
                                        </div>
                                        <div className="executive-summary-tile-selection-search non-draggable">
                                            <span className="dropdown-item build_search_drp mr-1">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search"
                                                    value={search}
                                                    onChange={e => setSearch(e.target.value)}
                                                    disabled={isLoading}
                                                />
                                                <i className={`fas fa-times cursor-pointer cls-close mb-1`} onClick={() => setSearch("")} />
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className="close executive-summary-tile-selection-close non-draggable"
                                            onClick={handleCancelClick}
                                        >
                                            <span aria-hidden="true">
                                                <img src="/img/close.svg" alt="" />
                                            </span>
                                        </button>
                                    </div>
                                    <div className="modal-body executive-summary-tile-selection-body non-draggable">
                                        <LoadingOverlay active={isLoading} spinner={<Loader />} fadeSpeed={10}>
                                            <div className="executive-summary-tile-selection-table-wrap es-hier-sel-wrap">
                                                <div className="es-hier-sel-card">
                                                    <div className="es-hier-sel-card-toolbar">
                                                        <div className="es-hier-sel-card-toolbar-title">Bands</div>
                                                        <label className="container-check green-check mb-0 es-hier-sel-band-check es-hier-sel-toolbar-all">
                                                            <input
                                                                type="checkbox"
                                                                ref={globalSelectAllRef}
                                                                checked={isAllSelected}
                                                                disabled={!availableIds.length}
                                                                onChange={e => toggleAllBands(e.target.checked)}
                                                            />
                                                            <span className="checkmark" />
                                                            <span className="es-hier-sel-toolbar-all-label">Select All</span>
                                                        </label>
                                                    </div>
                                                    <div className="es-hier-sel-card-body">
                                                        {filteredForest?.length ? (
                                                            <HierarchyTree
                                                                nodes={filteredForest}
                                                                depth={0}
                                                                selected={displaySelected}
                                                                onToggle={toggleNode}
                                                                disabledIds={disabledIdSet}
                                                                collapsedIds={collapsedIds}
                                                                onToggleExpand={handleToggleExpand}
                                                                onToggleInnerSelectAll={toggleInnerSelectAll}
                                                            />
                                                        ) : (
                                                            <div className="executive-summary-tile-selection-empty p-3">
                                                                No executive summary bands found
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </LoadingOverlay>
                                    </div>
                                    <div className="modal-footer executive-summary-tile-selection-footer non-draggable">
                                        <button type="button" className="btn btn-secondary btnClr" onClick={handleCancelClick}>
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary executive-summary-tile-selection-save"
                                            disabled={isLoading || !hasSelectionChanged}
                                            onClick={handleAddSelected}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Draggable>
                    </div>
                }
                onCancel={handleCancelClick}
            />
            {showCancelConfirmation ? (
                <Portal
                    body={
                        <ConfirmationModal
                            type="cancel"
                            heading={"Do you want to cancel and lose all changes?"}
                            message={"This action cannot be reverted, are you sure that you need to cancel?"}
                            onNo={() => setShowCancelConfirmation(false)}
                            onYes={handleConfirmCancel}
                            onCloseModal={() => setShowCancelConfirmation(false)}
                        />
                    }
                    onCancel={() => setShowCancelConfirmation(false)}
                    zIndex={10000}
                />
            ) : null}
        </>
    );
};

export default ExecutiveSummaryTileSelectionModal;
